import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { get, patch } from '../../../../../utils/api'; // Adjust the import path as needed
import { Box, Button, Checkbox, FormControlLabel, Typography, Paper, CircularProgress } from '@mui/material';

type ApiMethod = {
  method: string;
  enabled: boolean;
  public: boolean;
  middlewares: string[];
};

type ApiMethodsResponse = {
  api_methods?: ApiMethod[];
};

const ApiMethodEditor: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [methods, setMethods] = useState<ApiMethod[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    const fetchApiMethods = async () => {
      try {
        setLoading(true);
        const response = await get(`/field_settings/${id}`, {});
        if (response.data.api_methods) {
          setMethods(response.data.api_methods);
        } else {
          throw new Error("No API methods found");
        }
      } catch (error) {
        setError('Failed to fetch API methods');
        console.error('Failed to fetch API methods:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchApiMethods();
  }, [id]);

  const updateApiMethod = async () => {
    try {
      await patch(`/field_settings/${id}`, { api_methods: methods });
    } catch (error) {
      console.error('Failed to update API methods:', error);
    }
  };

  if (loading) {
    return <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh"><CircularProgress /></Box>;
  }

  if (error) {
    return <Typography color="error">{error}</Typography>;
  }

  return (
    <Box sx={{ p: 3 }}>
      {methods.map((method, index) => (
        <Paper key={index} elevation={3} sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6">{method.method}</Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={method.enabled}
                onChange={() => setMethods(methods.map((m, idx) => idx === index ? { ...m, enabled: !m.enabled } : m))}
              />
            }
            label="Enabled"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={method.public}
                onChange={() => setMethods(methods.map((m, idx) => idx === index ? { ...m, public: !m.public } : m))}
              />
            }
            label="Public"
          />
          <Box sx={{ display: 'flex', flexDirection: 'column', ml: 2 }}>
            <Typography variant="subtitle1">Middlewares:</Typography>
            {method.middlewares.map((middleware, idx) => (
              <FormControlLabel
                key={idx}
                control={
                  <Checkbox
                    checked={method.middlewares.includes(middleware)}
                    onChange={() => {
                      const newMiddlewares = [...method.middlewares];
                      const middlewareIndex = method.middlewares.indexOf(middleware);
                      if (middlewareIndex > -1) {
                        newMiddlewares.splice(middlewareIndex, 1);
                      } else {
                        newMiddlewares.push(middleware);
                      }
                      const newMethods = [...methods];
                      newMethods[index] = { ...method, middlewares: newMiddlewares };
                      setMethods(newMethods);
                    }}
                  />
                }
                label={middleware}
              />
            ))}
          </Box>
        </Paper>
      ))}
      <Box sx={{ position: 'fixed', right: 16, bottom: 16 }}>
        <Button variant="contained" color="primary" onClick={updateApiMethod}>
          Update
        </Button>
      </Box>
    </Box>
  );
};

export default ApiMethodEditor;
